import type { RefCallback } from 'react';

type CssVarValues = Record<string, string | number | undefined>;

/**
 * Imperatively write CSS custom properties onto an element. `undefined` removes
 * the property; numbers are written as pixels; strings are written verbatim.
 *
 * Atoms strips the inline `style` prop at the type level, so runtime-dynamic
 * values reach the DOM as CSS variables (set here via a ref) and are consumed
 * from Tailwind classNames (e.g. `grid-cols-(--table-columns)`).
 *
 * Mirrors cake's `src/shared/helpers/cssVars.ts`; worth hoisting into a shared
 * package if a third consumer appears.
 */
function setCssVars(el: ElementCSSInlineStyle, vars: CssVarValues): void {
  for (const name in vars) {
    const value = vars[name];
    if (value == null) {
      el.style.removeProperty(name);
    } else {
      el.style.setProperty(name, typeof value === 'number' ? `${value}px` : value);
    }
  }
}

/** Ref-callback form of {@link setCssVars} for use directly as an element `ref`. */
export function cssVars<T extends ElementCSSInlineStyle>(vars: CssVarValues): RefCallback<T> {
  return (el) => {
    if (el) {
      setCssVars(el, vars);
    }
  };
}
